import { useEffect, useState, useContext } from 'react';
import axios from '../utils/axios';

import { FormContext, FormDispatchContext } from '../contexts/FormContext';
import CriticalError from '../components/CriticalError';

import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormWrapper from '../components/FormWrapper';
import StaticTitle from '../components/questions/StaticTitle';
import StaticBody from '../components/questions/StaticBody';
import StaticImprovements from '../components/questions/StaticImprovements';
import StaticContact from '../components/questions/StaticContact';
import StaticText from '../components/questions/StaticText';
import Divider from '../components/questions/Divider';
import Dropdown from '../components/questions/Dropdown';
import YesNo from '../components/questions/YesNo';
import Freetext from '../components/questions/Freetext';
import MultipleChoice from '../components/questions/MultipleChoice';
import TreatmentType from '../components/questions/TreatmentType';
import StaffFilter from '../components/questions/StaffFilter';
import FFT from '../components/questions/FFT';
import NPS from '../components/questions/NPS';
import CrossSellServices from '../components/questions/CrossSellServices';
import SubmitFormSection from '../components/questions/SubmitFormSection';
import StarRating from '../components/questions/StarRating';

import { AnimatedGreenTick } from '../components/EmbeddedImages';
import '../styles/form.css';

export default function FeedbackForm(props) {
 
    const [ error, setError ] = useState('');
    const [ loading, setLoading ] = useState(true);
    const settings = useContext(FormContext);
    const settingsUpdate = useContext(FormDispatchContext);

    // Load in the company data and question set
    useEffect( () => {         
        // Ensure that the company ID is numeric
        if (! /^[0-9]+$/.test(props?.companyID)) {
            setError('Invalid company ID');
            setLoading(false);
        }
    }, [ props?.companyID ]);

    useEffect( () => { 

        setLoading(true);
        // setError('');
        const queryParameters = new URLSearchParams(window.location.search);

        if ( props?.qrCode === true ) { 
            settingsUpdate({ type: 'set_medium', medium: 'qrcode' });
        }

        // For renders where we already have inviteData, set everything up now        
        if ( props?.inviteData?.hasOwnProperty('questionSet') )
        {            
            settingsUpdate({ type: 'set_company', company: props?.inviteData?.company });
            settingsUpdate({ type: 'set_invite_id', inviteID: props?.inviteData?.id?.toString() });
            settingsUpdate({ type: 'set_medium', medium: props?.inviteData?.type?.toString().toLowerCase() });
            settingsUpdate({ type: 'set_questions', questionSet: props?.inviteData?.questionSet?.questions });

            settingsUpdate({ type: 'set_author_name', authorName: props?.inviteData?.author });
            settingsUpdate({ type: 'set_author_email', authorEmail: props?.inviteData?.destination?.obfuscated });
            settingsUpdate({ type: 'set_author_business_name', authorBusinessName: props?.inviteData?.businessName });
            settingsUpdate({ type: 'set_author_job_title', authorJobTitle: props?.inviteData?.jobTitle });
            settingsUpdate({ type: 'set_treatment_type', treatmentType: props?.inviteData?.treatmentType });

            settingsUpdate({ type: 'set_healthcare_type', healthcareType: props?.inviteData?.healthcareType });
            if ( props?.inviteData?.healthcareType !== 'Mixed'  ) {
                settingsUpdate({ type: 'set_treatment_type', treatmentType: props?.inviteData?.healthcareType });
            }

            settingsUpdate({ type: 'set_review_title', reviewTitle: props?.inviteData?.title });
            if ( props?.inviteData?.title?.length > 1 ) {
                settingsUpdate({ type: 'set_review_title_was_prefilled', value: true });
            } else {
                settingsUpdate({ type: 'set_review_title_was_prefilled', value: false });
            }
            // settingsUpdate({ type: 'set_staff_name', staffName: props?.inviteData?.staffName });
            // settingsUpdate({ type: 'set_source', staffName: props?.inviteData?.source });
            setLoading(false);
        }

        // For non-invite renders only, get the company details and question set
        if ( !props?.inviteData?.hasOwnProperty('questionSet') )
        {
            axios.get(`/company/${props?.companyID}/question-set?qrcode=${props?.qrCode === true ? '1' : '0'}`)
            .then((response) => { 
                settingsUpdate({ type: 'set_company', company: response.data?.company });
                settingsUpdate({ type: 'set_questions', questionSet: response.data?.questionSet?.questions });

                if ( response.data?.company?.healthcareType === 'Mixed' || response.data?.company?.healthcareType === 'Unknown' ) { 
                    settingsUpdate({ type: 'set_treatment_type', treatmentType: '-' });
                } else { 
                    settingsUpdate({ type: 'set_treatment_type', treatmentType: response.data?.company?.healthcareType });
                }        
            })
            .catch((error) => { 
                console.warn(error);
                if ( error?.message?.length > 1 ) { 
                    setError(error?.message);
                } else { 
                    setError('We can\'t display the feedback form at this time. Please refresh the page to try again.');
                }
                
            })
            .finally(() => { 
                setLoading(false);
            });
        }    

    }, [ props?.companyID, props?.inviteData ]);

    // TODO: Remove this block when we no longer need to debug in the console
    useEffect( () => { 
        console.log(settings);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings]);


    return ( 
        <Box>
            { loading && <LinearProgress mb={3} color="primaryGreen" /> }

            { !loading && error?.length > 0 && ( 
                <CriticalError message={error} />
            )}

            { !loading && settings?.complete && ( 
                <FormWrapper withCard={props?.withCard}>
                    <div style={{ textAlign: 'center' }}>
                        <img src={AnimatedGreenTick} alt="Green tick showing success" style={{ marginLeft: 'auto', marginRight: 'auto' }} />
                        <Typography variant="h5" className="workingfeedback-h5" mb={3}>
                            Thanks for leaving feedback for {settings?.company?.name}
                        </Typography>
                        <Typography variant="body2" className="workingfeedback-text" mb={3}>
                            { settings?.medium === 'online' && ( 
                                <>A verification email has been sent to {settings?.authorEmail} containing a link that you must follow to verify your 
                                review and complete your feedback submission.&nbsp;</>
                            )}
                            { settings?.medium !== 'online' && ( 
                                <>Your feedback has been submitted successfully.&nbsp; </>
                            )}
                            { settings?.redirectURL?.length > 0 && ( 
                                <><strong><br /><br />Please wait (or <a href={settings?.redirectURL}>click here</a> if you are not automatically redirected)</strong></>
                            )}
                        </Typography>
                    </div>
                </FormWrapper>
            )}

            { !loading && !error && !settings?.complete && ( 
                <FormWrapper withCard={props?.withCard}>

                    { props?.skipIntro !== true && (
                        <>
                            <Typography variant="h5" className="workingfeedback-h5" sx={{ marginBottom: '3px' }}>
                                Leave your opinion on {settings?.company?.name}
                            </Typography>
                            <Typography variant="body2" className="workingfeedback-text" mb={3}>
                                Your comments and feedback are always of great value to {settings?.company?.name}, so please let them know your thoughts. You can choose to remain anonymous if you wish.
                            </Typography>  
                        </>
                    )}  

                    <Stack direction="column" spacing={3}>
                    { settings?.questionSet?.map( (question, i) => { 
                        if ( question.type === 'STATIC_TITLE' ) return ( <StaticTitle key={i} question={question} /> )
                        if ( question.type === 'STATIC_BODY' ) return ( <StaticBody key={i} question={question} /> )
                        if ( question.type === 'STATIC_IMPROVEMENTS' ) return ( <StaticImprovements key={i} question={question} /> )
                        if ( question.type === 'STATIC_CONTACT' ) return ( <StaticContact key={i} question={question} /> )
                        if ( question.type === 'STATIC_TEXT' ) return ( <StaticText key={i} question={question} /> )
                        if ( question.type === 'DIVIDER' ) return ( <Divider key={i} /> )
                        if ( question.type === 'DROP_DOWN' ) return ( <Dropdown key={i} question={question} /> )
                        if ( question.type === 'YES_NO' ) return ( <YesNo key={i} question={question} /> )
                        if ( question.type === 'FREETEXT' ) return ( <Freetext key={i} question={question} /> )
                        if ( question.type === 'MULTIPLE_CHOICE' ) return ( <MultipleChoice key={i} question={question} /> )
                        if ( question.type === 'STAR_RATING' ) return ( <StarRating key={i} question={question} /> )
                        if ( question.type === 'NPS_RATING' ) return ( <NPS key={i} question={question} /> )
                        if ( question.type === 'TREATMENT_TYPE' ) return ( <TreatmentType key={i} question={question} /> )
                        if ( question.type === 'STAFF_FILTER' ) return ( <StaffFilter key={i} question={question} /> )
                        if ( question.type === 'FFT_QUESTION' ) return ( <FFT key={i} question={question} /> )
                        if ( question.type === 'CROSS_SELL_SERVICES' ) return ( <CrossSellServices key={i} question={question} /> )
                        return ( <></> );
                        // DEBUG ONLY FOR MISSING QUESTION TYPES: return ( <p key={i}>{ question?.type }</p> );
                    }) }
                    <SubmitFormSection />
                    </Stack>                
                </FormWrapper>
            )}            
        </Box> 
    )
    
}